import React, { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import PLayer from 'containers/player/Player'
import Loader from 'containers/loader/loader'
import OneLineContent from 'containers/RenderStroyComponent/OneLineContent'
import { CorrectionView, buildCorrectionMarkup, markupToText } from 'utils/correctionDiff'
import CorrectionText from './CorrectionText'
import { formatCorrectionDate } from './correctionDates'
import { useChineseLang } from '../../i18n/chinese'
import { storyTitleText } from '../../i18n/localizedContent'

export interface ClosedCorrection {
  storyId?: string
  createdAt?: number
  level?: string
  answer?: string
  correctedAnswer?: string
  answerContect?: any[]
  correctedAnswerContect?: any[]
  recordingUrl?: string
  correctedRecordingUrl?: string
}

interface Props {
  correction: ClosedCorrection
  isOpen: boolean
  onToggle: () => void
  /** The story and question behind this correction, fetched only once it opens. */
  isLoading: boolean
  loadError: boolean
  story?: any
  question?: string
  questionContent?: any[]
  chineseWordFontSize?: any
  activeIndex?: string
  onClickWord?: (event: any, word: any, sentence: any, key: string) => void
  recordingUrl?: string
  correctedRecordingUrl?: string
}

const VIEWS: { id: CorrectionView; label: string }[] = [
  { id: 'markup', label: 'corrections:card.views.markup' },
  { id: 'written', label: 'corrections:card.views.written' },
  { id: 'corrected', label: 'corrections:card.views.corrected' },
]

const PREVIEW_CHARS = 60

/**
 * One correction the teacher has finished, on the same expandable row the past
 * stories list uses.
 *
 * Collapsed it has to answer "which one is this?" — the old row showed a bare
 * date, so a list of them was a list of dates. Open, it answers "what did the
 * teacher change?" in one place: the marked-up text. The learner's original and
 * the clean corrected copy are that same markup with one side hidden, which is
 * why they are a switch here rather than the three near-identical paragraphs
 * the page used to stack.
 */
const CorrectionCard: React.FC<Props> = ({
  correction,
  isOpen,
  onToggle,
  isLoading,
  loadError,
  story,
  question,
  questionContent,
  chineseWordFontSize,
  activeIndex,
  onClickWord,
  recordingUrl,
  correctedRecordingUrl,
}) => {
  const { t } = useTranslation('corrections')
  const zhLang = useChineseLang()
  const [view, setView] = useState<CorrectionView>('markup')

  const markup = useMemo(
    () => buildCorrectionMarkup(correction?.answerContect, correction?.correctedAnswerContect),
    [correction?.answerContect, correction?.correctedAnswerContect],
  )

  const hasWriting = !!correction?.answer?.trim()
  const hasSpeaking = !!correction?.recordingUrl
  const hasWrittenCorrection = !!correction?.correctedAnswer?.trim()
  const hasSpokenCorrection = !!correction?.correctedRecordingUrl
  const changed = markup.changeCount > 0
  const switchable = hasWrittenCorrection && changed

  const date =
    typeof correction?.createdAt === 'number' ? formatCorrectionDate(correction.createdAt) : t('card.dateFallback')
  const preview = (markupToText(markup.runs, 'written') || correction?.answer || '').trim()
  const storyTitle = storyTitleText(story)
  const idPrefix = `CORRECTION-${correction?.storyId ?? 'row'}`

  // Plain words in the row rather than a row of badges: this is a quiet
  // sentence about the submission, not a set of controls.
  const meta = [
    hasWriting && hasSpeaking
      ? t('card.kind.both')
      : hasWriting
        ? t('card.kind.writing')
        : hasSpeaking
          ? t('card.kind.speaking')
          : null,
    changed ? t('card.changeCount', { count: markup.changeCount }) : null,
    !changed && hasWrittenCorrection ? t('card.noChanges') : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const audio = [
    recordingUrl && hasSpeaking ? { key: 'yours', label: t('card.yourRecording'), src: recordingUrl } : null,
    correctedRecordingUrl && hasSpokenCorrection
      ? { key: 'teacher', label: t('card.teacherRecording'), src: correctedRecordingUrl }
      : null,
  ].filter(Boolean) as { key: string; label: string; src: string }[]

  return (
    <div className='correction-item'>
      <div className='w-row w-d-flex section-story-content mb-20'>
        <div className='w-col-12 w-col-medium-12 w-col-small-12 w-col-tiny-12 content-left box-padding border-full border-radius-10'>
          <div className='answer-box box-border'>
            <div className='correction-item-show cx-row' onClick={onToggle}>
              <span className='box-title'>
                <span>{date}</span>
                {preview && (
                  <span className='cx-row-preview' lang={zhLang} translate='no'>
                    {preview.length > PREVIEW_CHARS ? `${preview.slice(0, PREVIEW_CHARS)}…` : preview}
                  </span>
                )}
              </span>
              <span className='cx-row-end'>
                {meta && <span className='cx-row-meta'>{meta}</span>}
                <span className='correction-date-downarrow'>
                  <i className={isOpen ? 'fa fa-chevron-up' : 'fa fa-chevron-down'} aria-hidden='true' />
                </span>
              </span>
            </div>
          </div>

          {isOpen &&
            (isLoading ? (
              <Loader />
            ) : (
              <div className='cx-body correction-common-padding border-top'>
                {/* The story this answer belongs to. The old page printed the
                    story's first line here with no label at all, which read as
                    a stray fragment of someone else's Chinese. */}
                {(story?.storyTitle || loadError) && (
                  <div className='cx-section cx-story'>
                    {loadError ? (
                      <p className='cx-quiet'>{t('card.storyLoadError')}</p>
                    ) : (
                      <>
                        <span className='cx-label'>{t('card.story')}</span>
                        <span className='cx-story-title' lang={zhLang} translate='no'>
                          {story.storyTitle}
                        </span>
                        {storyTitle.text && (
                          <span className='cx-quiet' lang={storyTitle.lang}>
                            {storyTitle.text}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                )}

                {questionContent?.length ? (
                  <div className='cx-section'>
                    <span className='box-title mb-10'>{t('card.question')}</span>
                    <div className='cx-question word_wrapper' lang={zhLang}>
                      {questionContent.map((wordObj, index) => (
                        <OneLineContent
                          key={index}
                          WIndex={index}
                          iterator={`${idPrefix}-QUESTION`}
                          chineseWordFontSize={chineseWordFontSize}
                          wordObj={wordObj}
                          contentDetail={questionContent}
                          story={story}
                          showPinYin={true}
                          activeIndex={activeIndex}
                          handleClickWord={onClickWord}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  question && (
                    <div className='cx-section'>
                      <span className='box-title mb-10'>{t('card.question')}</span>
                      <p className='cx-question-plain' lang={zhLang} translate='no'>
                        {question}
                      </p>
                    </div>
                  )
                )}

                {markup.runs.length > 0 && (
                  <div className='cx-section'>
                    <div className='cx-section-head'>
                      <span className='box-title'>{t('card.yourWriting')}</span>
                      {switchable && (
                        <div className='cx-views' role='group' aria-label={t('card.views.label')}>
                          {VIEWS.map((option) => (
                            <button
                              key={option.id}
                              type='button'
                              className={view === option.id ? 'active' : ''}
                              aria-pressed={view === option.id}
                              onClick={() => setView(option.id)}
                            >
                              {t(option.label)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {switchable && view === 'markup' && (
                      <p className='cx-key'>
                        <Trans
                          t={t}
                          i18nKey='card.markupKey'
                          components={{
                            added: <span className='cx-key-added' />,
                            removed: <span className='cx-key-removed' />,
                          }}
                        />
                      </p>
                    )}

                    <CorrectionText
                      runs={markup.runs}
                      view={switchable ? view : 'corrected'}
                      idPrefix={idPrefix}
                      activeIndex={activeIndex}
                      onClickWord={onClickWord}
                      fontSize={chineseWordFontSize?.storyContent}
                    />

                    {hasWrittenCorrection && !changed && (
                      <p className='cx-quiet cx-quiet--after'>{t('card.unchanged')}</p>
                    )}
                    {!hasWrittenCorrection && hasSpokenCorrection && (
                      <p className='cx-quiet cx-quiet--after'>{t('card.byVoice')}</p>
                    )}
                  </div>
                )}

                {markup.notes.length > 0 && (
                  <div className='cx-section'>
                    <span className='box-title mb-10'>{t('card.teacherNotes', { count: markup.notes.length })}</span>
                    {markup.notes.map((note, index) => (
                      <p key={index} className='cx-note' lang={zhLang}>
                        {note}
                      </p>
                    ))}
                  </div>
                )}

                {audio.length > 0 && (
                  <div className='cx-section'>
                    <span className='box-title mb-10'>{t('card.audio')}</span>
                    <div className='cx-audio'>
                      {audio.map((track) => (
                        <div className='cx-audio-col' key={track.key}>
                          <span className='cx-label'>{track.label}</span>
                          <PLayer audio={track.src} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!markup.runs.length && !audio.length && (
                  <p className='cx-quiet'>{t('card.empty')}</p>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

export default CorrectionCard
